import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Message from '../../../components/ui/Message';
import { AuthContext } from '../../../context/auth';
import { TablesContext } from '../../../context/table/TableContext';
import { Table } from '../../../models/interfaces';
import {
    buildLayoutPayload, canvasHeightFor, clampPosition, GRID_STEP, gridBackgroundOffset, isUnplaced, isValidSeats, layoutChanged,
    LayoutTable, MAX_SEATS, MIN_SEATS, overlappingIds, placeAtCenter, placeTable, removeFromPlan, scaleFor,
    setSeats, setShape, SHAPES, snapPosition, TableShape, tableSize, toNormalized, toPixels,
} from './floorPlan';
import './tableLayout.css';

// Clave del selector de sección ('none' = mesas sin sección)
const NO_SECTION = 'none';
// Vista general (solo lectura) con el plano de todas las secciones
const ALL_SECTIONS = 'all';
const HISTORY_LIMIT = 50;

const SHAPE_LABELS: Record<TableShape, { label: string; icon: string }> = {
    round: { label: 'Redonda', icon: 'bi-circle' },
    square: { label: 'Cuadrada', icon: 'bi-square' },
    rect: { label: 'Rectangular', icon: 'bi-aspect-ratio' },
};

const sectionKeyOf = (table: Table) => table.section?.id != null ? String(table.section.id) : NO_SECTION;

const toLayoutTable = (table: Table): LayoutTable => ({
    id: table.id,
    name: table.name,
    posX: table.posX ?? null,
    posY: table.posY ?? null,
    shape: (table.shape as TableShape) ?? null,
    seats: table.seats ?? null,
});

type History = { past: LayoutTable[][]; present: LayoutTable[]; future: LayoutTable[][] };

// Plano de una sección en miniatura (vista "Todas las secciones")
const SectionOverview = ({ title, tables, posPreview, onOpen }: {
    title: string;
    tables: LayoutTable[];
    posPreview: boolean;
    onOpen: () => void;
}) => {
    const ref = useRef<HTMLDivElement>(null);
    const [width, setWidth] = useState(240);

    useEffect(() => {
        const element = ref.current;
        if (!element) return;
        const update = () => setWidth(element.clientWidth || 240);
        update();
        const observer = new ResizeObserver(update);
        observer.observe(element);
        return () => observer.disconnect();
    }, []);

    const height = canvasHeightFor(width);
    const scale = scaleFor(width);
    const offset = gridBackgroundOffset(width);
    const placed = tables.filter(t => !isUnplaced(t));
    const unplacedCount = tables.length - placed.length;

    return (
        <div className="card border-0 shadow-sm h-100">
            <div className="card-body d-flex flex-column">
                <div className="d-flex justify-content-between align-items-start mb-2 gap-2">
                    <div>
                        <div className="fw-semibold">{title}</div>
                        <div className="text-muted small">
                            {placed.length} en el plano{unplacedCount > 0 ? ` · ${unplacedCount} sin ubicar` : ''}
                        </div>
                    </div>
                    <button type="button" className="btn btn-sm btn-outline-primary text-nowrap" onClick={onOpen}>
                        <i className="bi bi-pencil me-1" /> Editar
                    </button>
                </div>
                <div
                    ref={ref}
                    className={`floor-canvas${posPreview ? ' is-preview' : ''}`}
                    style={{
                        height,
                        cursor: 'pointer',
                        backgroundSize: `${GRID_STEP * scale}px ${GRID_STEP * scale}px`,
                        backgroundPosition: `${offset.x}px ${offset.y}px`,
                    }}
                    title={`Editar el plano de ${title}`}
                    onClick={onOpen}
                >
                    {placed.map(table => {
                        const size = tableSize(table.shape, width);
                        const center = toPixels(table.posX!, table.posY!, width, height);
                        return (
                            <div
                                key={table.id}
                                className={`floor-table${posPreview ? ' pos-free' : ''}`}
                                style={{
                                    left: center.x - size.width / 2,
                                    top: center.y - size.height / 2,
                                    width: size.width,
                                    height: size.height,
                                    borderRadius: size.borderRadius,
                                    fontSize: Math.max(7, 11 * scale),
                                    cursor: 'pointer',
                                }}
                                title={table.name}
                            >
                                <span className="floor-table-name">{table.name}</span>
                            </div>
                        );
                    })}
                    {placed.length === 0 && (
                        <div className="position-absolute top-50 start-50 translate-middle text-center text-muted small px-3">
                            Sin mesas en el plano
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

type BackendError = { message: string; details?: { tableId: number | null; field: string; message: string }[] };

export const TableLayoutPage = () => {
    const { tables, sections, saveLayout } = useContext(TablesContext);
    const { resources } = useContext(AuthContext);
    const navigate = useNavigate();

    // Mismo permiso que el backend: recurso "tables" (administración de mesas)
    const canEdit = (resources ?? []).some(r => r.code === 'tables');

    // -------------------- Secciones --------------------

    const sectionOptions = useMemo(() => {
        const options: { key: string; label: string; sectionId: number | null; count: number }[] = [];
        const counts = new Map<string, number>();
        tables.forEach(t => counts.set(sectionKeyOf(t), (counts.get(sectionKeyOf(t)) ?? 0) + 1));

        const known = new Map<string, string>();
        (sections ?? []).forEach(s => known.set(String(s.id), s.name));
        tables.forEach(t => { if (t.section?.id != null) known.set(String(t.section.id), t.section.name); });

        known.forEach((name, key) => options.push({ key, label: name, sectionId: Number(key), count: counts.get(key) ?? 0 }));
        options.sort((a, b) => a.label.localeCompare(b.label));
        if (counts.get(NO_SECTION)) options.push({ key: NO_SECTION, label: 'Sin sección', sectionId: null, count: counts.get(NO_SECTION)! });
        if (options.length > 1) options.unshift({ key: ALL_SECTIONS, label: 'Todas las secciones', sectionId: null, count: tables.length });
        return options;
    }, [tables, sections]);

    const [sectionKey, setSectionKey] = useState<string | null>(null);
    const isAll = sectionKey === ALL_SECTIONS;
    const currentSection = isAll ? null : sectionOptions.find(o => o.key === sectionKey) ?? null;

    // Primera sección con mesas
    useEffect(() => {
        if (sectionKey === null && sectionOptions.length > 0) {
            // Por defecto, la vista general; si hay una sola sección, directamente su editor
            setSectionKey(sectionOptions[0].key);
        }
    }, [sectionOptions, sectionKey]);

    // -------------------- Estado del editor (con deshacer/rehacer) --------------------

    const [saved, setSaved] = useState<LayoutTable[]>([]);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [history, setHistory] = useState<History>({ past: [], present: [], future: [] });
    const present = history.present;
    const dirty = layoutChanged(present, saved);

    const sectionTables = useMemo(
        () => sectionKey === null ? [] : tables.filter(t => sectionKeyOf(t) === sectionKey).map(toLayoutTable),
        [tables, sectionKey]
    );

    // Carga la sección (al cambiarla o al refrescar las mesas, si no hay cambios pendientes)
    const loadedKey = useRef<string | null>(null);
    useEffect(() => {
        if (sectionKey === null) return;
        if (sectionKey === ALL_SECTIONS) {
            // Vista general: no hay nada que editar
            loadedKey.current = sectionKey;
            setSaved([]);
            setHistory({ past: [], present: [], future: [] });
            setSelectedId(null);
            return;
        }
        if (loadedKey.current === sectionKey && dirty) return;
        loadedKey.current = sectionKey;
        setSaved(sectionTables);
        setHistory({ past: [], present: sectionTables, future: [] });
        setSelectedId(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sectionKey, sectionTables]);

    const commit = useCallback((next: LayoutTable[]) => {
        setHistory(h => ({ past: [...h.past, h.present].slice(-HISTORY_LIMIT), present: next, future: [] }));
    }, []);

    const undo = useCallback(() => setHistory(h => h.past.length === 0 ? h
        : { past: h.past.slice(0, -1), present: h.past[h.past.length - 1], future: [h.present, ...h.future] }), []);

    const redo = useCallback(() => setHistory(h => h.future.length === 0 ? h
        : { past: [...h.past, h.present], present: h.future[0], future: h.future.slice(1) }), []);

    // -------------------- UI --------------------

    const [snap, setSnap] = useState(true);
    const [posPreview, setPosPreview] = useState(false);
    const [busyPreview, setBusyPreview] = useState<Set<number>>(new Set());
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<BackendError | null>(null);
    const [success, setSuccess] = useState('');
    const [pendingAction, setPendingAction] = useState<{ message: string; run: () => void } | null>(null);

    const selected = present.find(t => t.id === selectedId) ?? null;
    const placed = present.filter(t => !isUnplaced(t));
    const unplaced = present.filter(isUnplaced);
    const overlaps = useMemo(() => overlappingIds(present), [present]);
    const invalidSeats = present.filter(t => !isValidSeats(t.seats));

    // -------------------- Lienzo --------------------

    const canvasRef = useRef<HTMLDivElement>(null);
    const [canvasWidth, setCanvasWidth] = useState(360);
    const canvasHeight = canvasHeightFor(canvasWidth);
    const scale = scaleFor(canvasWidth);
    const gridOffset = gridBackgroundOffset(canvasWidth);

    useEffect(() => {
        const element = canvasRef.current;
        if (!element) return;
        const update = () => setCanvasWidth(element.clientWidth || 360);
        update();
        const observer = new ResizeObserver(update);
        observer.observe(element);
        return () => observer.disconnect();
    }, [sectionKey, posPreview]);

    // Posición (normalizada) de un punto de la pantalla dentro del lienzo, o null si está fuera
    const pointToCanvas = (clientX: number, clientY: number, allowOutside = false) => {
        const rect = canvasRef.current?.getBoundingClientRect();
        if (!rect) return null;
        const inside = clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
        if (!inside && !allowOutside) return null;
        return toNormalized(clientX - rect.left, clientY - rect.top, rect.width, rect.height);
    };

    const constrain = (posX: number, posY: number, shape: TableShape | null) =>
        snap ? snapPosition(posX, posY, shape) : clampPosition(posX, posY, shape);

    // Arrastre de una mesa del lienzo (mouse y táctil con Pointer Events)
    const drag = useRef<{ id: number; offsetX: number; offsetY: number; moved: boolean } | null>(null);
    const [dragPosition, setDragPosition] = useState<{ id: number; posX: number; posY: number } | null>(null);

    const onTablePointerDown = (e: React.PointerEvent, table: LayoutTable) => {
        if (posPreview) return;
        e.preventDefault();
        e.stopPropagation();
        setSelectedId(table.id);
        const point = pointToCanvas(e.clientX, e.clientY, true);
        if (!point) return;
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        drag.current = { id: table.id, offsetX: point.posX - table.posX!, offsetY: point.posY - table.posY!, moved: false };
    };

    const onTablePointerMove = (e: React.PointerEvent, table: LayoutTable) => {
        const state = drag.current;
        if (!state || state.id !== table.id) return;
        const point = pointToCanvas(e.clientX, e.clientY, true);
        if (!point) return;
        state.moved = true;
        setDragPosition({ id: table.id, ...constrain(point.posX - state.offsetX, point.posY - state.offsetY, table.shape) });
    };

    const onTablePointerUp = (e: React.PointerEvent) => {
        const state = drag.current;
        drag.current = null;
        if (state?.moved && dragPosition && dragPosition.id === state.id) {
            commit(placeTable(present, state.id, dragPosition.posX, dragPosition.posY, snap));
        }
        setDragPosition(null);
        try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch { /* sin captura */ }
    };

    // Arrastre desde "Mesas sin ubicar" hacia el lienzo (clic = colocar en el centro)
    const chipDrag = useRef<{ id: number; startX: number; startY: number; moved: boolean } | null>(null);
    const [ghost, setGhost] = useState<{ id: number; x: number; y: number; overCanvas: boolean } | null>(null);

    const onChipPointerDown = (e: React.PointerEvent, table: LayoutTable) => {
        e.preventDefault();
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        chipDrag.current = { id: table.id, startX: e.clientX, startY: e.clientY, moved: false };
    };

    const onChipPointerMove = (e: React.PointerEvent) => {
        const state = chipDrag.current;
        if (!state) return;
        if (!state.moved && Math.hypot(e.clientX - state.startX, e.clientY - state.startY) < 5) return;
        state.moved = true;
        setGhost({ id: state.id, x: e.clientX, y: e.clientY, overCanvas: pointToCanvas(e.clientX, e.clientY) !== null });
    };

    const onChipPointerUp = (e: React.PointerEvent) => {
        const state = chipDrag.current;
        chipDrag.current = null;
        setGhost(null);
        try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch { /* sin captura */ }
        if (!state) return;

        if (!state.moved) {
            commit(placeAtCenter(present, state.id, snap));
            setSelectedId(state.id);
            return;
        }
        const point = pointToCanvas(e.clientX, e.clientY);
        if (point) {
            commit(placeTable(present, state.id, point.posX, point.posY, snap));
            setSelectedId(state.id);
        }
    };

    // -------------------- Atajos de teclado --------------------

    useEffect(() => {
        if (posPreview) return;
        const onKeyDown = (e: KeyboardEvent) => {
            const target = e.target as HTMLElement;
            if (target && ['INPUT', 'SELECT', 'TEXTAREA'].includes(target.tagName)) return;
            const mod = e.ctrlKey || e.metaKey;
            if (mod && e.key.toLowerCase() === 'z' && !e.shiftKey) { e.preventDefault(); undo(); }
            else if (mod && (e.key.toLowerCase() === 'y' || (e.key.toLowerCase() === 'z' && e.shiftKey))) { e.preventDefault(); redo(); }
            else if ((e.key === 'Delete' || e.key === 'Backspace') && selectedId !== null) {
                const table = present.find(t => t.id === selectedId);
                if (table && !isUnplaced(table)) { e.preventDefault(); commit(removeFromPlan(present, selectedId)); }
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [posPreview, undo, redo, selectedId, present, commit]);

    // -------------------- Cambios sin guardar --------------------

    // Cerrar o recargar la pestaña
    useEffect(() => {
        if (!dirty) return;
        const onBeforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [dirty]);

    // Navegar a otra pantalla desde un enlace (menú, etc.)
    useEffect(() => {
        if (!dirty) return;
        const onClick = (e: MouseEvent) => {
            const anchor = (e.target as HTMLElement)?.closest?.('a[href]') as HTMLAnchorElement | null;
            if (!anchor || anchor.target === '_blank' || e.ctrlKey || e.metaKey) return;
            const url = new URL(anchor.href, window.location.href);
            if (url.origin !== window.location.origin || url.pathname === window.location.pathname) return;
            e.preventDefault();
            e.stopPropagation();
            setPendingAction({
                message: 'Hay cambios sin guardar en el plano. ¿Desea salir y descartarlos?',
                run: () => navigate(url.pathname + url.search),
            });
        };
        document.addEventListener('click', onClick, true);
        return () => document.removeEventListener('click', onClick, true);
    }, [dirty, navigate]);

    const changeSection = (key: string) => {
        if (key === sectionKey) return;
        const run = () => { loadedKey.current = null; setSaveError(null); setSectionKey(key); };
        if (dirty) {
            setPendingAction({ message: 'Hay cambios sin guardar en esta sección. ¿Desea cambiar de sección y descartarlos?', run });
        } else {
            run();
        }
    };

    const discard = () => {
        setHistory({ past: [], present: saved, future: [] });
        setSelectedId(null);
        setSaveError(null);
    };

    // -------------------- Guardar --------------------

    const save = async () => {
        if (!currentSection) return;
        if (invalidSeats.length > 0) {
            setSaveError({ message: `Revise la cantidad de sillas (entre ${MIN_SEATS} y ${MAX_SEATS}) de: ${invalidSeats.map(t => t.name).join(', ')}.` });
            return;
        }
        setSaving(true);
        setSaveError(null);
        const resp = await saveLayout(buildLayoutPayload(currentSection.sectionId, present));
        setSaving(false);

        if (resp.error) {
            // Se conservan los cambios locales para reintentar
            setSaveError({
                message: resp.status === 403 ? 'No tiene permiso para modificar el plano de mesas.' : resp.error,
                details: resp.details,
            });
            return;
        }
        const updated = (resp.tables ?? []).map(toLayoutTable);
        setSaved(updated);
        setHistory({ past: [], present: updated, future: [] });
        setSuccess('El plano de la sección se guardó correctamente.');
    };

    const tableName = (id: number | null) => present.find(t => t.id === id)?.name ?? (id !== null ? `Mesa ${id}` : 'General');

    // -------------------- Render --------------------

    if (!canEdit) {
        return (
            <div className="mt-5 container-fluid px-4">
                <div className="card border-0 shadow-sm">
                    <div className="card-body text-center py-5">
                        <i className="bi bi-lock text-muted" style={{ fontSize: 32 }} />
                        <h6 className="mt-2 mb-1">No tiene acceso al plano de mesas</h6>
                        <p className="text-muted small mb-0">Solo los usuarios con permiso de administración de mesas pueden diseñar el plano.</p>
                    </div>
                </div>
            </div>
        );
    }

    const renderTable = (table: LayoutTable) => {
        const isDragging = dragPosition?.id === table.id;
        const posX = isDragging ? dragPosition!.posX : table.posX!;
        const posY = isDragging ? dragPosition!.posY : table.posY!;
        const size = tableSize(table.shape, canvasWidth);
        const center = toPixels(posX, posY, canvasWidth, canvasHeight);
        const busy = busyPreview.has(table.id);

        const classes = ['floor-table'];
        if (posPreview) classes.push(busy ? 'pos-busy' : 'pos-free');
        else {
            if (selectedId === table.id) classes.push('is-selected');
            if (isDragging) classes.push('is-dragging');
            if (overlaps.has(table.id)) classes.push('is-overlapping');
        }

        return (
            <div
                key={table.id}
                className={classes.join(' ')}
                style={{
                    left: center.x - size.width / 2,
                    top: center.y - size.height / 2,
                    width: size.width,
                    height: size.height,
                    borderRadius: size.borderRadius,
                    fontSize: Math.max(8, 11 * scale),
                }}
                title={table.name}
                onPointerDown={(e) => onTablePointerDown(e, table)}
                onPointerMove={(e) => onTablePointerMove(e, table)}
                onPointerUp={onTablePointerUp}
                onPointerCancel={onTablePointerUp}
                onClick={posPreview ? () => setBusyPreview(prev => {
                    const next = new Set(prev);
                    next.has(table.id) ? next.delete(table.id) : next.add(table.id);
                    return next;
                }) : undefined}
            >
                <span className="floor-table-name">{table.name}</span>
                {!posPreview && table.seats ? (
                    <span className="floor-table-seats" style={{ fontSize: Math.max(7, 9 * scale) }}>
                        <i className="bi bi-person" /> {table.seats}
                    </span>
                ) : null}
            </div>
        );
    };

    const ghostTable = ghost ? present.find(t => t.id === ghost.id) : null;
    const ghostSize = ghostTable ? tableSize(ghostTable.shape, canvasWidth) : null;

    return (
        <div className="mt-5 container-fluid px-4" style={{ flex: 1, marginTop: 10 }}>

            <div className="d-flex flex-wrap justify-content-between align-items-start mb-4 gap-2">
                <div>
                    <h3 className="mb-1">Plano de mesas</h3>
                    <p className="text-muted mb-0">Ubique las mesas de cada sección tal como se verán en el POS.</p>
                </div>
                <div className="d-flex gap-2 flex-wrap">
                    <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/tables')}>
                        <i className="bi bi-arrow-left me-1" /> Volver a mesas
                    </button>
                    <button type="button" className="btn btn-outline-secondary" onClick={discard} disabled={!dirty || saving}>
                        Descartar cambios
                    </button>
                    <button type="button" className="btn btn-primary" onClick={save} disabled={!dirty || saving || !currentSection}>
                        {saving ? <><span className="spinner-border spinner-border-sm me-1" /> Guardando...</> : <><i className="bi bi-check-lg me-1" /> Guardar</>}
                    </button>
                </div>
            </div>

            {/* Barra de herramientas */}
            <div className="card border-0 shadow-sm mb-3">
                <div className="card-body d-flex flex-wrap align-items-center gap-3">
                    <div className="d-flex align-items-center gap-2">
                        <label className="small text-muted mb-0" htmlFor="layoutSection">Sección</label>
                        <select id="layoutSection" className="form-select" style={{ minWidth: 200 }} value={sectionKey ?? ''} onChange={(e) => changeSection(e.target.value)}>
                            {sectionOptions.map(o => <option key={o.key} value={o.key}>{o.label} ({o.count})</option>)}
                        </select>
                    </div>

                    {!posPreview && !isAll && (
                        <>
                            <div className="btn-group" role="group" aria-label="Historial">
                                <button type="button" className="btn btn-outline-secondary" title="Deshacer (Ctrl+Z)" onClick={undo} disabled={history.past.length === 0}>
                                    <i className="bi bi-arrow-counterclockwise" />
                                </button>
                                <button type="button" className="btn btn-outline-secondary" title="Rehacer (Ctrl+Y)" onClick={redo} disabled={history.future.length === 0}>
                                    <i className="bi bi-arrow-clockwise" />
                                </button>
                            </div>
                            <div className="form-check form-switch mb-0">
                                <input className="form-check-input" type="checkbox" role="switch" id="layoutSnap" checked={snap} onChange={(e) => setSnap(e.target.checked)} />
                                <label className="form-check-label small" htmlFor="layoutSnap">Ajustar a la cuadrícula</label>
                            </div>
                        </>
                    )}

                    <div className="form-check form-switch mb-0 ms-md-auto">
                        <input className="form-check-input" type="checkbox" role="switch" id="layoutPreview" checked={posPreview}
                            onChange={(e) => { setPosPreview(e.target.checked); setSelectedId(null); setBusyPreview(new Set()); }} />
                        <label className="form-check-label small" htmlFor="layoutPreview"><i className="bi bi-phone me-1" />Vista</label>
                    </div>

                    {dirty && <span className="badge rounded-pill bg-warning-subtle text-warning-emphasis">Cambios sin guardar</span>}
                </div>
            </div>

            {saveError && (
                <div className="alert alert-danger d-flex gap-2 align-items-start">
                    <i className="bi bi-exclamation-circle mt-1" />
                    <div className="small">
                        <div className="fw-semibold">{saveError.message}</div>
                        {saveError.details && saveError.details.length > 0 && (
                            <ul className="mb-1 mt-1 ps-3">
                                {saveError.details.map((d, i) => <li key={i}><span className="fw-medium">{tableName(d.tableId)}:</span> {d.message}</li>)}
                            </ul>
                        )}
                        <div className="text-muted">Sus cambios se mantienen; corrija e intente guardar de nuevo.</div>
                    </div>
                    <button type="button" className="btn-close ms-auto" onClick={() => setSaveError(null)} />
                </div>
            )}

            {sectionOptions.length === 0 ? (
                <div className="card border-0 shadow-sm">
                    <div className="card-body text-center py-5 text-muted">No hay mesas configuradas. Cree mesas desde la pantalla de Mesas.</div>
                </div>
            ) : isAll ? (
                <div className="row g-3">
                    {sectionOptions.filter(o => o.key !== ALL_SECTIONS).map(option => (
                        <div className="col-12 col-sm-6 col-lg-4 col-xxl-3" key={option.key}>
                            <SectionOverview
                                title={option.label}
                                tables={tables.filter(t => sectionKeyOf(t) === option.key).map(toLayoutTable)}
                                posPreview={posPreview}
                                onOpen={() => changeSection(option.key)}
                            />
                        </div>
                    ))}
                </div>
            ) : (
                <div className="row g-3">
                    {/* Lienzo */}
                    <div className={posPreview ? 'col-12' : 'col-12 col-lg-7 col-xl-6'}>
                        <div className="card border-0 shadow-sm h-100">
                            <div className="card-body">
                                {posPreview && (
                                    <div className="d-flex flex-wrap justify-content-center gap-3 small text-muted mb-3">
                                        <span><span className="d-inline-block rounded-circle me-1 align-middle" style={{ width: 12, height: 12, border: '2px solid #78909c' }} />Libre</span>
                                        <span><span className="d-inline-block rounded-circle me-1 align-middle" style={{ width: 12, height: 12, background: 'linear-gradient(135deg, #1CABFF, #0F88E2)' }} />Con órdenes activas</span>
                                        <span><i className="bi bi-hand-index me-1" />Toque una mesa para simular que está ocupada</span>
                                    </div>
                                )}
                                <div className="floor-canvas-wrapper">
                                    <div
                                        ref={canvasRef}
                                        className={`floor-canvas${posPreview ? ' is-preview' : ''}${ghost?.overCanvas ? ' is-drop-target' : ''}`}
                                        style={{
                                            height: canvasHeight,
                                            backgroundSize: `${GRID_STEP * scale}px ${GRID_STEP * scale}px`,
                                            backgroundPosition: `${gridOffset.x}px ${gridOffset.y}px`,
                                        }}
                                        onPointerDown={() => !posPreview && setSelectedId(null)}
                                    >
                                        {placed.map(renderTable)}
                                        {placed.length === 0 && (
                                            <div className="position-absolute top-50 start-50 translate-middle text-center text-muted small px-4">
                                                {posPreview ? 'No hay mesas ubicadas en esta sección.' : 'Arrastre aquí las mesas del panel "Mesas sin ubicar".'}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {!posPreview && overlaps.size > 0 && (
                                    <div className="alert alert-warning py-2 small mt-3 mb-0">
                                        <i className="bi bi-exclamation-triangle me-1" />
                                        Hay mesas encimadas: {present.filter(t => overlaps.has(t.id)).map(t => t.name).join(', ')}.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Paneles laterales */}
                    {!posPreview && (
                        <div className="col-12 col-lg-5 col-xl-6">
                            <div className="card border-0 shadow-sm mb-3">
                                <div className="card-body">
                                    <h6 className="mb-3">Propiedades</h6>
                                    {selected ? (
                                        <>
                                            <div className="fw-semibold mb-3">{selected.name}</div>
                                            <label className="form-label small text-muted mb-1">Forma</label>
                                            <div className="btn-group w-100 mb-3" role="group">
                                                {SHAPES.map(shape => (
                                                    <button key={shape} type="button"
                                                        className={`btn ${(selected.shape ?? 'round') === shape ? 'btn-primary' : 'btn-outline-primary'}`}
                                                        onClick={() => commit(setShape(present, selected.id, shape))}>
                                                        <i className={`bi ${SHAPE_LABELS[shape].icon} me-1`} />{SHAPE_LABELS[shape].label}
                                                    </button>
                                                ))}
                                            </div>
                                            <label className="form-label small text-muted mb-1" htmlFor="layoutSeats">Sillas</label>
                                            <input
                                                id="layoutSeats"
                                                type="number"
                                                min={MIN_SEATS}
                                                max={MAX_SEATS}
                                                step={1}
                                                className={`form-control mb-1 ${isValidSeats(selected.seats) ? '' : 'is-invalid'}`}
                                                style={{ maxWidth: 160 }}
                                                placeholder="Sin definir"
                                                value={selected.seats ?? ''}
                                                onChange={(e) => commit(setSeats(present, selected.id, e.target.value === '' ? null : Number(e.target.value)))}
                                            />
                                            <div className={`small mb-3 ${isValidSeats(selected.seats) ? 'text-muted' : 'text-danger'}`}>Entre {MIN_SEATS} y {MAX_SEATS}, o vacío.</div>
                                            {!isUnplaced(selected) ? (
                                                <button type="button" className="btn btn-outline-danger" onClick={() => { commit(removeFromPlan(present, selected.id)); }}>
                                                    <i className="bi bi-box-arrow-left me-1" /> Quitar del plano
                                                </button>
                                            ) : (
                                                <button type="button" className="btn btn-outline-primary" onClick={() => commit(placeAtCenter(present, selected.id, snap))}>
                                                    <i className="bi bi-bullseye me-1" /> Colocar en el centro
                                                </button>
                                            )}
                                        </>
                                    ) : (
                                        <p className="text-muted small mb-0">Seleccione una mesa del plano para cambiar su forma y sillas.</p>
                                    )}
                                </div>
                            </div>

                            <div className="card border-0 shadow-sm">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <h6 className="mb-0">Mesas sin ubicar</h6>
                                        <span className="badge rounded-pill bg-light text-muted border">{unplaced.length}</span>
                                    </div>
                                    {unplaced.length > 0 ? (
                                        <>
                                            <p className="text-muted small">Arrástrelas al plano o haga clic para colocarlas en el centro.</p>
                                            <div className="d-flex flex-column gap-2">
                                                {unplaced.map(table => (
                                                    <button
                                                        key={table.id}
                                                        type="button"
                                                        className="unplaced-chip"
                                                        onPointerDown={(e) => onChipPointerDown(e, table)}
                                                        onPointerMove={onChipPointerMove}
                                                        onPointerUp={onChipPointerUp}
                                                        onPointerCancel={() => { chipDrag.current = null; setGhost(null); }}
                                                    >
                                                        <i className={`bi ${SHAPE_LABELS[(table.shape ?? 'round') as TableShape].icon} text-muted`} />
                                                        <span className="flex-grow-1 fw-medium">{table.name}</span>
                                                        {table.seats ? <span className="text-muted small"><i className="bi bi-person" /> {table.seats}</span> : null}
                                                        <i className="bi bi-grip-vertical text-muted" />
                                                    </button>
                                                ))}
                                            </div>
                                        </>
                                    ) : (
                                        <p className="text-muted small mb-0">Todas las mesas de la sección están en el plano.</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Mesa que se arrastra desde el panel */}
            {ghost && ghostTable && ghostSize && (
                <div
                    className="floor-table floor-drag-ghost"
                    style={{ left: ghost.x, top: ghost.y, width: ghostSize.width, height: ghostSize.height, borderRadius: ghostSize.borderRadius, fontSize: Math.max(8, 11 * scale) }}
                >
                    <span className="floor-table-name">{ghostTable.name}</span>
                </div>
            )}

            <div style={{ height: '60px' }} />

            {pendingAction && (
                <Message
                    type="success"
                    title="Cambios sin guardar"
                    message={pendingAction.message}
                    closeDialog={() => setPendingAction(null)}
                    showCancel={true}
                    handleCancel={() => setPendingAction(null)}
                    handleOk={() => { const action = pendingAction; setPendingAction(null); action.run(); }}
                    backgroundColor="rgba(0,0,0,0.4)"
                />
            )}
            {success && (
                <Message type="success" title="Exitoso" message={success} closeDialog={() => setSuccess('')} handleOk={() => setSuccess('')} backgroundColor="rgba(0,0,0,0.4)" />
            )}
        </div>
    );
};
